import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  Checkbox,
  FormGroup,
  ListItemText,
  Stepper,
  Step,
  StepButton,
  TableSortLabel,
  LinearProgress,
} from '@mui/material';
import {
  Add,
  Delete,
  Campaign as CampaignIcon,
  LibraryMusic,
  Upload as UploadIcon,
  PlayArrow,
  Stop,
  MusicNote,
  Visibility,
  ArrowBack,
  ArrowForward,
  Check,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import AudioEditor from './AudioEditor';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const toIdList = (value) =>
  parseJsonArray(value)
    .map((id) => (typeof id === 'number' ? id : Number(id)))
    .filter((id) => Number.isFinite(id));

const hasId = (list, id) => (list || []).some((item) => Number(item) === Number(id));

const toHourList = (value) =>
  parseJsonArray(value)
    .map((h) => parseInt(h, 10))
    .filter((h) => Number.isInteger(h) && h >= 0 && h <= 23);

const getSortComparator = (order, orderBy, getValue) => (a, b) => {
  const aVal = getValue ? getValue(a) : (a[orderBy] ?? '');
  const bVal = getValue ? getValue(b) : (b[orderBy] ?? '');
  const isAsc = order === 'asc';
  if (typeof aVal === 'number' && typeof bVal === 'number') return isAsc ? aVal - bVal : bVal - aVal;
  const cmp = String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, { numeric: true });
  return isAsc ? cmp : -cmp;
};

const DEFAULT_SPOT_FORM = {
  name: '',
  duration: 30,
  is_placeholder: false,
  voice_artist: '',
  business_category_id: '',
  exclude_from_total: false,
  use_as_filler: false,
};

const DEFAULT_SEGMENT_FORM = {
  name: '', start_date: '', end_date: '', never_end: false, total_spots: 0, spots_per_day: '', run_in_all_breaks: false,
  selected_hours: [], selected_breaks: [], weekdays: [0, 1, 2, 3, 4, 5, 6], daypart_ids: [],
  positioning: 'any', spot_selection: 'random', segment_template_id: '',
  specific_spot_id: '', spot_percentages: {}, all_breaks: true,
};

const STEPS = [
  { label: 'Campaign Details', icon: CampaignIcon },
  { label: 'Spots', icon: LibraryMusic },
  { label: 'Schedule', icon: ScheduleIcon },
];

// Consistent section card used inside each wizard step
const Section = ({ title, description, accent = '#22d3ee', children, sx = {} }) => (
  <Box
    sx={{
      p: 2.5,
      bgcolor: 'var(--rs-surface)',
      borderRadius: 2,
      border: '1px solid var(--rs-border)',
      mb: 2,
      ...sx,
    }}
  >
    {title && (
      <Typography variant="subtitle2" sx={{ color: accent, mb: description ? 0.5 : 1.5, fontWeight: 700, letterSpacing: '0.02em' }}>
        {title}
      </Typography>
    )}
    {description && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {description}
      </Typography>
    )}
    {children}
  </Box>
);

/**
 * CampaignWizard — unified New/Edit Campaign flow.
 * Step 1: campaign details → Step 2: upload spots → Step 3: schedule (segments).
 * The campaign is persisted when leaving step 1 so spots/segments can attach to it.
 */
export default function CampaignWizard({
  open,
  onClose,
  campaign = null,
  initialStep = 0,
  stations = [],
  regions = [],
  breaks = [],
  franchiseTargets = [],
  businessCategories = [],
  dayparts = [],
  segmentTemplates = [],
  canEdit = false,
  onSaved,
  onTemplatesChanged,
  onToast,
}) {
  const notify = useCallback(
    (msg) => { if (typeof onToast === 'function') onToast(msg); },
    [onToast]
  );

  const [step, setStep] = useState(0);
  const [wizardCampaign, setWizardCampaign] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // Spots state
  const [campaignSpots, setCampaignSpots] = useState([]);
  const [loadingSpots, setLoadingSpots] = useState(false);
  const [spotForm, setSpotForm] = useState(DEFAULT_SPOT_FORM);
  const [spotFile, setSpotFile] = useState(null);
  const [uploadingSpot, setUploadingSpot] = useState(false);
  const [playingSpotId, setPlayingSpotId] = useState(null);
  const spotAudioRef = useRef(null);
  const [replacingSpotId, setReplacingSpotId] = useState(null);
  const [replaceTargetSpotId, setReplaceTargetSpotId] = useState(null);
  const replaceSpotInputRef = useRef(null);
  const [spotCueEditorOpen, setSpotCueEditorOpen] = useState(false);
  const [editingCueSpot, setEditingCueSpot] = useState(null);
  const [spotSort, setSpotSort] = useState({ orderBy: 'name', order: 'asc' });

  // Segments state
  const [campaignSegments, setCampaignSegments] = useState([]);
  const [segmentForm, setSegmentForm] = useState(DEFAULT_SEGMENT_FORM);
  const [segmentSort, setSegmentSort] = useState({ orderBy: 'name', order: 'asc' });
  const [selectedSegmentDetail, setSelectedSegmentDetail] = useState(null);
  const [addingSegment, setAddingSegment] = useState(false);
  const fetchGenRef = useRef(0);

  const isEdit = !!campaign?.id;
  const readOnly = !canEdit || !!wizardCampaign?.is_syndicated;

  const stopSpotPreview = useCallback(() => {
    setPlayingSpotId(null);
    if (spotAudioRef.current) {
      spotAudioRef.current.pause();
      spotAudioRef.current = null;
    }
  }, []);

  const fetchSpots = useCallback(async (campaignId, gen) => {
    if (!campaignId) return;
    setLoadingSpots(true);
    try {
      const res = await axios.get(`${API_URL}/api/advertising/campaigns/${campaignId}/spots`, { withCredentials: true });
      if (gen != null && fetchGenRef.current !== gen) return;
      setCampaignSpots(res.data || []);
    } catch (error) {
      console.error('Error fetching campaign spots:', error);
      if (gen != null && fetchGenRef.current !== gen) return;
      setCampaignSpots([]);
    } finally {
      if (gen == null || fetchGenRef.current === gen) setLoadingSpots(false);
    }
  }, []);

  const fetchSegments = useCallback(async (campaignId, gen) => {
    if (!campaignId) return;
    try {
      const res = await axios.get(`${API_URL}/api/advertising/campaigns/${campaignId}/segments`, { withCredentials: true });
      if (gen != null && fetchGenRef.current !== gen) return;
      setCampaignSegments(res.data || []);
    } catch (error) {
      console.error('Error fetching campaign segments:', error);
      if (gen != null && fetchGenRef.current !== gen) return;
      setCampaignSegments([]);
    }
  }, []);

  // Initialise the wizard every time it is opened
  useEffect(() => {
    if (!open) return;
    const gen = ++fetchGenRef.current;
    const c = campaign || null;
    setWizardCampaign(c);
    setStep(c ? (Number(initialStep) || 0) : 0);
    setFormData(c ? {
      name: c.name || '',
      client: c.client || '',
      scheduling_mode: c.scheduling_mode || 'variable',
      status: c.status || 'scheduled',
      start_date: c.start_date ? String(c.start_date).split('T')[0] : '',
      end_date: c.end_date ? String(c.end_date).split('T')[0] : '',
      enable_stations: toIdList(c.enable_stations),
      enable_regions: toIdList(c.enable_regions),
      selected_hours: toHourList(c.selected_hours),
      stop_after_plays: c.stop_after_plays || '',
      total_budget: c.total_budget || '',
      business_category_id: c.business_category_id || '',
      franchise_distribution: c.franchise_distribution || 'none',
      franchise_link_ids: parseJsonArray(c.franchise_link_ids),
    } : {
      name: '',
      client: '',
      scheduling_mode: 'variable',
      status: 'scheduled',
      start_date: '',
      end_date: '',
      enable_stations: stations.length ? [stations[0].id] : [],
      enable_regions: [],
      selected_hours: [],
      stop_after_plays: '',
      total_budget: '',
      business_category_id: '',
      franchise_distribution: 'none',
      franchise_link_ids: [],
    });
    setCampaignSpots([]);
    setCampaignSegments([]);
    setSpotForm(DEFAULT_SPOT_FORM);
    setSpotFile(null);
    setSegmentForm(DEFAULT_SEGMENT_FORM);
    setSelectedSegmentDetail(null);
    setSpotCueEditorOpen(false);
    setEditingCueSpot(null);
    stopSpotPreview();
    if (c?.id) {
      fetchSpots(c.id, gen);
      fetchSegments(c.id, gen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCloseWizard = () => {
    if (saving || uploadingSpot || addingSegment) return;
    stopSpotPreview();
    setSpotCueEditorOpen(false);
    setEditingCueSpot(null);
    setSelectedSegmentDetail(null);
    onClose();
  };

  // Stop any audio preview when the wizard closes
  useEffect(() => {
    if (!open) {
      stopSpotPreview();
      setSpotCueEditorOpen(false);
      setEditingCueSpot(null);
      setSelectedSegmentDetail(null);
    }
  }, [open, stopSpotPreview]);

  const createSortHandler = (setState) => (field) => () => {
    setState((prev) => ({
      orderBy: field,
      order: prev.orderBy === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  // ---------- Step 1: campaign details ----------

  const handleSaveDetails = async () => {
    if (readOnly) return true; // nothing to save in read-only mode
    if (!formData.name?.trim()) {
      notify('Please enter a campaign name');
      return false;
    }
    const campaignData = { ...formData, name: formData.name.trim() };
    ['total_budget', 'total_spots', 'stop_after_plays', 'business_category_id', 'start_date', 'end_date'].forEach((k) => {
      if (campaignData[k] === '' || campaignData[k] == null) delete campaignData[k];
    });
    setSaving(true);
    try {
      if (wizardCampaign?.id) {
        const res = await axios.put(
          `${API_URL}/api/advertising/campaigns/${wizardCampaign.id}`,
          campaignData,
          { withCredentials: true }
        );
        setWizardCampaign(res.data || { ...wizardCampaign, ...campaignData });
      } else {
        const res = await axios.post(`${API_URL}/api/advertising/campaigns`, campaignData, { withCredentials: true });
        setWizardCampaign(res.data);
      }
      if (typeof onSaved === 'function') onSaved();
      return true;
    } catch (error) {
      console.error('Error saving campaign:', error);
      const msg = error.response?.data?.message || error.message;
      const detail = error.response?.data?.detail;
      notify('Failed to save: ' + msg + (detail ? ` (${detail})` : ''));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDetailsNext = async () => {
    const ok = await handleSaveDetails();
    if (ok) setStep(1);
  };

  const goToStep = async (target) => {
    if (target === step) return;
    if (step === 0 && target > 0 && !readOnly) {
      const ok = await handleSaveDetails();
      if (!ok) return;
      setStep(target);
      return;
    }
    if (!wizardCampaign?.id && target > 0) return;
    setStep(target);
  };

  // ---------- Step 2: spots ----------

  const handleAddSpot = async () => {
    if (!wizardCampaign?.id) return;
    const { name, duration, is_placeholder } = spotForm;
    if (!name?.trim()) {
      notify('Please enter a spot name');
      return;
    }
    if (!is_placeholder && !spotFile) {
      notify('Please select an audio file, or check "Placeholder" for spots without audio yet.');
      return;
    }
    setUploadingSpot(true);
    try {
      const formDataSpot = new FormData();
      formDataSpot.append('name', name.trim());
      formDataSpot.append('duration', String(duration || 30));
      formDataSpot.append('weight', '100');
      formDataSpot.append('is_placeholder', (spotFile ? false : is_placeholder) ? 'true' : 'false');
      formDataSpot.append('exclude_from_total', spotForm.exclude_from_total ? 'true' : 'false');
      formDataSpot.append('use_as_filler', spotForm.use_as_filler ? 'true' : 'false');
      if (spotForm.voice_artist) formDataSpot.append('voice_artist', spotForm.voice_artist);
      if (spotForm.business_category_id) formDataSpot.append('business_category_id', spotForm.business_category_id);
      if (spotFile) formDataSpot.append('audio', spotFile);
      await axios.post(
        `${API_URL}/api/advertising/campaigns/${wizardCampaign.id}/spots/upload`,
        formDataSpot,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      fetchSpots(wizardCampaign.id);
      setSpotForm(DEFAULT_SPOT_FORM);
      setSpotFile(null);
    } catch (error) {
      console.error('Error adding spot:', error);
      notify('Failed to add spot: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploadingSpot(false);
    }
  };

  const handlePlaySpot = (spot) => {
    if (!spot.filepath) return;
    const isSame = playingSpotId === spot.id;
    if (spotAudioRef.current) {
      spotAudioRef.current.pause();
      spotAudioRef.current = null;
    }
    setPlayingSpotId(isSame ? null : spot.id);
    if (isSame) return;
    const normalizedPath = spot.filepath.startsWith('uploads/') ? spot.filepath : `uploads/${spot.filepath}`;
    const audio = new Audio(`${API_URL}/${normalizedPath}`);
    spotAudioRef.current = audio;
    audio.onended = () => { setPlayingSpotId(null); spotAudioRef.current = null; };
    audio.onerror = () => { setPlayingSpotId(null); spotAudioRef.current = null; };
    audio.play();
  };

  const handleOpenSpotCueEditor = (spot) => {
    if (!spot?.filepath) return;
    stopSpotPreview();
    setEditingCueSpot(spot);
    setSpotCueEditorOpen(true);
  };

  const handleSaveSpotCues = async (markers) => {
    if (!wizardCampaign?.id || !editingCueSpot) return;
    await axios.put(
      `${API_URL}/api/advertising/campaigns/${wizardCampaign.id}/spots/${editingCueSpot.id}`,
      {
        cue_in: markers.cue_in || 0,
        cue_out: markers.cue_out ?? null,
      },
      { withCredentials: true }
    );
    await fetchSpots(wizardCampaign.id);
    setEditingCueSpot((prev) => (prev ? { ...prev, cue_in: markers.cue_in || 0, cue_out: markers.cue_out ?? null } : prev));
  };

  const handleReplacePlaceholderAudio = async (spotId, file) => {
    if (!wizardCampaign?.id || !file) return;
    setReplacingSpotId(spotId);
    try {
      const fd = new FormData();
      fd.append('audio', file);
      await axios.put(
        `${API_URL}/api/advertising/campaigns/${wizardCampaign.id}/spots/${spotId}/upload`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      fetchSpots(wizardCampaign.id);
    } catch (e) {
      notify('Failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setReplacingSpotId(null);
      if (replaceSpotInputRef.current) replaceSpotInputRef.current.value = '';
    }
  };

  const handleDeleteSpot = async (spotId) => {
    if (!wizardCampaign?.id || !window.confirm('Delete this spot?')) return;
    try {
      await axios.delete(`${API_URL}/api/advertising/campaigns/${wizardCampaign.id}/spots/${spotId}`, { withCredentials: true });
      fetchSpots(wizardCampaign.id);
    } catch (error) {
      console.error('Error deleting spot:', error);
      notify('Failed to delete spot');
    }
  };

  const toggleSpotFlag = async (spot, field) => {
    if (!wizardCampaign?.id) return;
    const current = field === 'exclude_from_total'
      ? (spot.exclude_from_total === true || spot.exclude_from_total === 'true')
      : !!spot[field];
    try {
      await axios.put(
        `${API_URL}/api/advertising/campaigns/${wizardCampaign.id}/spots/${spot.id}`,
        { [field]: !current },
        { withCredentials: true }
      );
      fetchSpots(wizardCampaign.id);
    } catch {
      notify('Failed to update');
    }
  };

  // ---------- Step 3: segments (the schedule) ----------

  const handleAddSegment = async () => {
    if (!wizardCampaign?.id || !segmentForm.name || !segmentForm.start_date) {
      notify('Name and start date required');
      return;
    }
    if (!segmentForm.never_end && !segmentForm.end_date) {
      notify('End date required unless "Never end" is checked');
      return;
    }
    if (segmentForm.spot_selection === 'specific' && !segmentForm.specific_spot_id) {
      notify('Please select a specific spot');
      return;
    }
    if (segmentForm.spot_selection === 'percentage') {
      const total = Object.values(segmentForm.spot_percentages || {}).reduce((a, b) => a + (Number(b) || 0), 0);
      if (Math.abs(total - 100) > 1) {
        notify('Spot percentages must total 100%');
        return;
      }
    }
    if (!segmentForm.all_breaks && (segmentForm.selected_breaks || []).length === 0) {
      notify('Select at least one break, or enable "All breaks"');
      return;
    }
    setAddingSegment(true);
    try {
      await axios.post(`${API_URL}/api/advertising/campaigns/${wizardCampaign.id}/segments`, {
        ...segmentForm,
        daypart_ids: segmentForm.daypart_ids || [],
        never_end: !!segmentForm.never_end,
        end_date: segmentForm.never_end ? null : segmentForm.end_date,
        settings: { ...(segmentForm.settings || {}), run_in_all_breaks: !!segmentForm.run_in_all_breaks },
        selected_breaks: segmentForm.all_breaks ? [] : (segmentForm.selected_breaks || []),
        specific_spot_id: segmentForm.spot_selection === 'specific' ? segmentForm.specific_spot_id : null,
        spot_percentages: segmentForm.spot_selection === 'percentage' ? segmentForm.spot_percentages : {},
        total_spots: segmentForm.total_spots || 0,
        spots_per_day: segmentForm.run_in_all_breaks ? null : (segmentForm.spots_per_day ? parseInt(segmentForm.spots_per_day) : null),
        segment_template_id: segmentForm.segment_template_id || null,
      }, { withCredentials: true });
      fetchSegments(wizardCampaign.id);
      setSegmentForm(DEFAULT_SEGMENT_FORM);
    } catch (e) {
      notify('Failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setAddingSegment(false);
    }
  };

  const handleDeleteSegment = async (segmentId) => {
    if (!wizardCampaign?.id || !window.confirm('Delete this segment?')) return;
    try {
      await axios.delete(`${API_URL}/api/advertising/campaigns/${wizardCampaign.id}/segments/${segmentId}`, { withCredentials: true });
      fetchSegments(wizardCampaign.id);
    } catch {
      notify('Failed to delete');
    }
  };

  const handleSaveTemplate = async () => {
    const name = window.prompt('Template name:');
    if (!name) return;
    try {
      await axios.post(`${API_URL}/api/advertising/segment-templates`, {
        name,
        template_data: { ...segmentForm, spots_per_day: segmentForm.spots_per_day ? parseInt(segmentForm.spots_per_day) : null },
      }, { withCredentials: true });
      if (typeof onTemplatesChanged === 'function') onTemplatesChanged();
      notify('Template saved');
    } catch (e) {
      notify('Failed: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleFinish = () => {
    if (typeof onSaved === 'function') onSaved();
    notify(isEdit ? 'Campaign updated.' : `Campaign "${wizardCampaign?.name || ''}" is ready. Use Generate Schedule to place it into breaks.`);
    onClose();
  };

  // ---------- Render ----------

  const renderDetailsStep = () => (
    <Box>
      <Section
        title="Basics"
        description="Name the campaign and choose how the scheduler should place its spots."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Campaign Name *"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              disabled={readOnly}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Client"
              value={formData.client || ''}
              onChange={(e) => setFormData({ ...formData, client: e.target.value })}
              disabled={readOnly}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={readOnly}>
              <InputLabel>Scheduling Mode</InputLabel>
              <Select
                value={formData.scheduling_mode || 'variable'}
                label="Scheduling Mode"
                onChange={(e) => setFormData({ ...formData, scheduling_mode: e.target.value })}
              >
                <MenuItem value="static">Static (same hour/break daily)</MenuItem>
                <MenuItem value="variable">Variable (algorithm selects within dayparts)</MenuItem>
                <MenuItem value="manual">Manual (select hours & breaks)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={readOnly}>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status || 'scheduled'}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                {formData.status === 'paused' && <MenuItem value="paused">Paused</MenuItem>}
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={formData.start_date || ''}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={readOnly}
            />
          </Grid>
        </Grid>
      </Section>

      <Section
        title="Where it plays"
        description="Pick the stations this campaign runs on. Regional splits and franchise distribution are optional."
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'var(--rs-text)', fontWeight: 600 }}>Stations</Typography>
            <FormGroup row>
              {stations.map((s) => (
                <FormControlLabel
                  key={s.id}
                  control={
                    <Checkbox
                      disabled={readOnly}
                      checked={hasId(formData.enable_stations, s.id)}
                      onChange={(e) => {
                        const arr = formData.enable_stations || [];
                        setFormData({
                          ...formData,
                          enable_stations: e.target.checked
                            ? [...arr, s.id]
                            : arr.filter((id) => Number(id) !== Number(s.id)),
                        });
                      }}
                    />
                  }
                  label={s.name}
                />
              ))}
            </FormGroup>
          </Grid>

          {(formData.franchise_distribution || 'none') !== 'none'
            || franchiseTargets.some((link) =>
              hasId(formData.enable_stations, link.source_station_id)) ? (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" sx={{ mb: 1, color: '#c084fc', fontWeight: 600 }}>
                Separate-tenant franchise distribution
              </Typography>
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Mandatory spots are pushed only through active franchise links. Same-system stations and
                regional splits continue to use the controls above.
              </Alert>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Franchise distribution</InputLabel>
                <Select
                  value={formData.franchise_distribution || 'none'}
                  label="Franchise distribution"
                  onChange={(event) => setFormData({
                    ...formData,
                    franchise_distribution: event.target.value,
                    franchise_link_ids: event.target.value === 'selected'
                      ? (formData.franchise_link_ids || [])
                      : [],
                  })}
                >
                  <MenuItem value="none">Parent tenant only</MenuItem>
                  <MenuItem value="all">All eligible separate-tenant franchise stations</MenuItem>
                  <MenuItem value="selected">Selected separate-tenant franchise stations</MenuItem>
                </Select>
              </FormControl>
              {formData.franchise_distribution === 'selected' && (
                <FormGroup sx={{ mt: 1 }}>
                  {franchiseTargets
                    .filter((link) =>
                      hasId(formData.enable_stations, link.source_station_id))
                    .map((link) => (
                      <FormControlLabel
                        key={link.link_id}
                        control={(
                          <Checkbox
                            disabled={readOnly}
                            checked={(formData.franchise_link_ids || []).includes(link.link_id)}
                            onChange={(event) => {
                              const current = formData.franchise_link_ids || [];
                              setFormData({
                                ...formData,
                                franchise_link_ids: event.target.checked
                                  ? [...current, link.link_id]
                                  : current.filter((id) => id !== link.link_id),
                              });
                            }}
                          />
                        )}
                        label={`${link.child_tenant_name || link.child_tenant_id} · destination station ${link.destination_station_id}`}
                      />
                    ))}
                </FormGroup>
              )}
            </Grid>
          ) : null}

          {regions.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" sx={{ mb: 0.5, color: 'var(--rs-text)', fontWeight: 600 }}>Regions / Splits</Typography>
              <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1 }}>
                Select which regions this campaign should play in. Leave empty to play in all regions.
              </Typography>
              <FormGroup row>
                {regions.map((r) => (
                  <FormControlLabel
                    key={r.id}
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={hasId(formData.enable_regions, r.id)}
                        onChange={(e) => {
                          const arr = formData.enable_regions || [];
                          setFormData({
                            ...formData,
                            enable_regions: e.target.checked
                              ? [...arr, r.id]
                              : arr.filter((id) => Number(id) !== Number(r.id)),
                          });
                        }}
                      />
                    }
                    label={
                      <span>
                        {r.name}
                        {r.station_name && (
                          <span style={{ color: '#888', fontSize: '0.85em', marginLeft: 4 }}>
                            ({r.station_name})
                          </span>
                        )}
                      </span>
                    }
                  />
                ))}
              </FormGroup>
            </Grid>
          )}
        </Grid>
      </Section>

      {(formData.scheduling_mode || '') === 'manual' && (
        <Section title="Selected Hours (Manual)" description="Only used in manual scheduling mode.">
          <FormGroup row>
            {Array.from({ length: 24 }, (_, i) => (
              <FormControlLabel
                key={i}
                control={
                  <Checkbox
                    disabled={readOnly}
                    checked={hasId(formData.selected_hours, i)}
                    onChange={(e) => {
                      const arr = formData.selected_hours || [];
                      setFormData({
                        ...formData,
                        selected_hours: e.target.checked
                          ? [...arr, i]
                          : arr.filter((h) => Number(h) !== i),
                      });
                    }}
                  />
                }
                label={`${i.toString().padStart(2, '0')}:00`}
              />
            ))}
          </FormGroup>
        </Section>
      )}
    </Box>
  );

  const renderSpotsStep = () => (
    <Box>
      {readOnly ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {wizardCampaign?.is_syndicated
            ? 'Mandatory franchise spots are managed by the parent tenant and are read-only here.'
            : 'You have view-only access to advertising.'}
        </Alert>
      ) : (
        <Section
          title="Add Spot"
          description="Upload audio files or create placeholders for spots not yet produced. Duration is used for break scheduling (30s, 40s, etc). Playback uses the full file unless you set cue in / cue out."
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Spot Name"
                value={spotForm.name}
                onChange={(e) => setSpotForm({ ...spotForm, name: e.target.value })}
                placeholder="e.g. Client Ad 30s"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Duration (s)"
                type="number"
                inputProps={{ min: 0, max: 600 }}
                value={spotForm.duration}
                onChange={(e) => setSpotForm({ ...spotForm, duration: parseFloat(e.target.value) || 30 })}
                helperText="Break timing only. Audio is not cut to this length."
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={spotForm.exclude_from_total === true}
                    onChange={(e) => setSpotForm({ ...spotForm, exclude_from_total: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Exclude from total</Typography>
                    <Typography variant="caption" color="text.secondary">OFF = included (default). ON = duration counts as 0 in break totals.</Typography>
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" component="label" size="small" sx={{ borderColor: spotFile ? '#22c55e' : '#666', color: spotFile ? '#22c55e' : 'var(--rs-muted)' }}>
                  <UploadIcon fontSize="small" sx={{ mr: 0.5 }} />
                  {spotFile ? spotFile.name : 'Choose Audio'}
                  <input type="file" hidden accept=".mp3,.wav,.flac,.ogg,.opus,.m4a,.aac" onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setSpotFile(file);
                    if (file) setSpotForm((prev) => ({ ...prev, is_placeholder: false }));
                  }} />
                </Button>
                {!spotFile ? (
                  <>
                    <FormControlLabel
                      control={<Switch size="small" checked={spotForm.is_placeholder} onChange={(e) => setSpotForm({ ...spotForm, is_placeholder: e.target.checked })} />}
                      label="Placeholder"
                    />
                    <Typography variant="caption" color="text.secondary">(no audio yet)</Typography>
                  </>
                ) : (
                  <Typography variant="caption" sx={{ color: '#22c55e' }}>Audio selected – will upload</Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Voice Artist" value={spotForm.voice_artist || ''} onChange={(e) => setSpotForm({ ...spotForm, voice_artist: e.target.value })} placeholder="For voice separation" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Business Category</InputLabel>
                <Select value={spotForm.business_category_id || ''} onChange={(e) => setSpotForm({ ...spotForm, business_category_id: e.target.value })} label="Business Category">
                  <MenuItem value="">None</MenuItem>
                  {businessCategories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={spotForm.use_as_filler || false}
                    onChange={(e) => setSpotForm({ ...spotForm, use_as_filler: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Use as filler</Typography>
                    <Typography variant="caption" color="text.secondary">Can be used to top up short breaks to target duration</Typography>
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleAddSpot}
                disabled={uploadingSpot || !spotForm.name?.trim()}
                startIcon={<Add />}
                sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
              >
                {uploadingSpot ? 'Adding...' : 'Add Spot'}
              </Button>
            </Grid>
          </Grid>
        </Section>
      )}

      <input
        type="file"
        ref={replaceSpotInputRef}
        accept=".mp3,.wav,.flac,.ogg,.opus,.m4a,.aac"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f && replaceTargetSpotId) {
            handleReplacePlaceholderAudio(replaceTargetSpotId, f);
            setReplaceTargetSpotId(null);
          }
          e.target.value = '';
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: '#22d3ee', fontWeight: 700 }}>
          Spots ({campaignSpots.length})
        </Typography>
        {uploadingSpot && <LinearProgress sx={{ width: 120 }} />}
      </Box>
      {loadingSpots ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : campaignSpots.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed var(--rs-border)', borderRadius: 2 }}>
          <LibraryMusic sx={{ color: 'var(--rs-muted)', fontSize: 30, mb: 0.5 }} />
          <Typography color="text.secondary">
            {readOnly ? 'No spots on this campaign.' : 'No spots yet. Add your first spot above.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={spotSort.orderBy === 'name' ? spotSort.order : false}>
                  <TableSortLabel active={spotSort.orderBy === 'name'} direction={spotSort.orderBy === 'name' ? spotSort.order : 'asc'} onClick={createSortHandler(setSpotSort)('name')}>Name</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={spotSort.orderBy === 'duration' ? spotSort.order : false}>
                  <TableSortLabel active={spotSort.orderBy === 'duration'} direction={spotSort.orderBy === 'duration' ? spotSort.order : 'asc'} onClick={createSortHandler(setSpotSort)('duration')}>Duration</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={spotSort.orderBy === 'exclude_from_total' ? spotSort.order : false}>
                  <TableSortLabel active={spotSort.orderBy === 'exclude_from_total'} direction={spotSort.orderBy === 'exclude_from_total' ? spotSort.order : 'asc'} onClick={createSortHandler(setSpotSort)('exclude_from_total')}>Exclude from total</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={spotSort.orderBy === 'use_as_filler' ? spotSort.order : false}>
                  <TableSortLabel active={spotSort.orderBy === 'use_as_filler'} direction={spotSort.orderBy === 'use_as_filler' ? spotSort.order : 'asc'} onClick={createSortHandler(setSpotSort)('use_as_filler')}>Filler</TableSortLabel>
                </TableCell>
                <TableCell>Audio</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...campaignSpots].sort(getSortComparator(spotSort.order, spotSort.orderBy, (s) => {
                if (spotSort.orderBy === 'exclude_from_total') return (s.exclude_from_total === true || s.exclude_from_total === 'true') ? 1 : 0;
                if (spotSort.orderBy === 'use_as_filler') return s.use_as_filler ? 1 : 0;
                if (spotSort.orderBy === 'total_plays') return s.total_plays ?? 0;
                return s[spotSort.orderBy] ?? (spotSort.orderBy === 'duration' ? 0 : '');
              })).map((spot) => (
                <TableRow key={spot.id}>
                  <TableCell>{spot.name}</TableCell>
                  <TableCell>
                    <Box>
                      {spot.duration}s
                      {(Number(spot.cue_in) > 0 || Number(spot.cue_out) > 0) && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Cue {Number(spot.cue_in) > 0 ? Number(spot.cue_in).toFixed(1) : '0'}s → {Number(spot.cue_out) > 0 ? `${Number(spot.cue_out).toFixed(1)}s` : 'end'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          disabled={readOnly || spot.is_syndicated}
                          checked={spot.exclude_from_total === true || spot.exclude_from_total === 'true'}
                          onChange={() => toggleSpotFlag(spot, 'exclude_from_total')}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          {(spot.exclude_from_total === true || spot.exclude_from_total === 'true') ? 'Yes' : 'No (included)'}
                        </Typography>
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          disabled={readOnly || spot.is_syndicated}
                          checked={!!spot.use_as_filler}
                          onChange={() => toggleSpotFlag(spot, 'use_as_filler')}
                        />
                      }
                      label={<Typography variant="caption">{spot.use_as_filler ? 'Filler' : ''}</Typography>}
                    />
                  </TableCell>
                  <TableCell>
                    {(spot.is_placeholder && !spot.filepath) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip label="Placeholder" size="small" sx={{ bgcolor: '#666', color: 'var(--rs-muted)' }} />
                        <IconButton
                          size="small"
                          onClick={() => { setReplaceTargetSpotId(spot.id); replaceSpotInputRef.current?.click(); }}
                          disabled={readOnly || replacingSpotId === spot.id || spot.is_syndicated}
                          sx={{ color: '#22d3ee' }}
                          title="Upload audio to replace placeholder"
                        >
                          <UploadIcon fontSize="small" />
                        </IconButton>
                        {replacingSpotId === spot.id && <Typography variant="caption">Uploading…</Typography>}
                      </Box>
                    ) : spot.filepath ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip label="Uploaded" size="small" sx={{ bgcolor: '#22d3ee20', color: '#22d3ee' }} />
                        <IconButton
                          size="small"
                          onClick={() => handlePlaySpot(spot)}
                          sx={{ color: playingSpotId === spot.id ? '#22c55e' : '#22d3ee' }}
                          title={playingSpotId === spot.id ? 'Stop' : 'Play'}
                        >
                          {playingSpotId === spot.id ? <Stop fontSize="small" /> : <PlayArrow fontSize="small" />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenSpotCueEditor(spot)}
                          disabled={readOnly || spot.is_syndicated}
                          sx={{ color: '#22d3ee' }}
                          title="Set cue in / cue out"
                        >
                          <MusicNote fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      disabled={readOnly || spot.is_syndicated}
                      onClick={() => handleDeleteSpot(spot.id)}
                      sx={{ color: '#ff6b6b' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  const renderSegmentsStep = () => (
    <Box>
      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {wizardCampaign?.is_syndicated
            ? 'Mandatory franchise segments are managed by the parent tenant and are read-only here.'
            : 'You have view-only access to advertising.'}
        </Alert>
      )}
      {!readOnly && (
        <Section
          title="Add Segment"
          description="Define when and how often this campaign plays: date range, days, day parts, breaks and position. Segments run concurrently."
          accent="#c084fc"
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Name" value={segmentForm.name} onChange={(e) => setSegmentForm({ ...segmentForm, name: e.target.value })} placeholder="e.g. Morning Drive" />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField fullWidth size="small" label="Start" type="date" value={segmentForm.start_date} onChange={(e) => setSegmentForm({ ...segmentForm, start_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField fullWidth size="small" label="End" type="date" value={segmentForm.end_date} onChange={(e) => setSegmentForm({ ...segmentForm, end_date: e.target.value })} InputLabelProps={{ shrink: true }} disabled={segmentForm.never_end} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControlLabel control={<Switch checked={segmentForm.never_end} onChange={(e) => setSegmentForm({ ...segmentForm, never_end: e.target.checked, end_date: e.target.checked ? '' : segmentForm.end_date })} />} label="Never end" />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControlLabel
                control={<Switch checked={segmentForm.run_in_all_breaks} onChange={(e) => setSegmentForm({ ...segmentForm, run_in_all_breaks: e.target.checked })} />}
                label="Every break"
              />
            </Grid>
            {!segmentForm.run_in_all_breaks && (
              <Grid item xs={12} sm={2}>
                <TextField fullWidth size="small" label="Spots/day" type="number" value={segmentForm.spots_per_day} onChange={(e) => setSegmentForm({ ...segmentForm, spots_per_day: e.target.value })} />
              </Grid>
            )}
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Position in break</InputLabel>
                <Select value={segmentForm.positioning} onChange={(e) => setSegmentForm({ ...segmentForm, positioning: e.target.value })} label="Position in break">
                  <MenuItem value="any">Any</MenuItem>
                  <MenuItem value="first">First</MenuItem>
                  <MenuItem value="upper_third">Upper third</MenuItem>
                  <MenuItem value="middle">Middle</MenuItem>
                  <MenuItem value="lower_third">Lower third</MenuItem>
                  <MenuItem value="last">Last</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Spot selection</InputLabel>
                <Select value={segmentForm.spot_selection} onChange={(e) => setSegmentForm({ ...segmentForm, spot_selection: e.target.value, specific_spot_id: '', spot_percentages: {} })} label="Spot selection">
                  <MenuItem value="random">Random</MenuItem>
                  <MenuItem value="sequential">Sequential</MenuItem>
                  <MenuItem value="percentage">By percentage</MenuItem>
                  <MenuItem value="specific">Specific spot</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {segmentForm.spot_selection === 'specific' && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select spot</InputLabel>
                  <Select
                    value={segmentForm.specific_spot_id ?? ''}
                    onChange={(e) => setSegmentForm({ ...segmentForm, specific_spot_id: e.target.value || null })}
                    label="Select spot"
                  >
                    <MenuItem value="">—</MenuItem>
                    {campaignSpots.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name} ({s.duration}s){s.is_placeholder ? ' [placeholder]' : ''}
                      </MenuItem>
                    ))}
                    {campaignSpots.length === 0 && (
                      <MenuItem value="" disabled>No spots – add spots in the Spots step first</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {segmentForm.spot_selection === 'percentage' && campaignSpots.filter((s) => !s.is_placeholder).length > 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Spot percentages (must total 100%)</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {campaignSpots.filter((s) => !s.is_placeholder).map((s) => (
                    <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ minWidth: 80 }}>{s.name}:</Typography>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, max: 100, step: 5 }}
                        sx={{ width: 70 }}
                        value={segmentForm.spot_percentages?.[s.id] ?? 0}
                        onChange={(e) => {
                          const v = parseInt(e.target.value) || 0;
                          setSegmentForm({
                            ...segmentForm,
                            spot_percentages: { ...segmentForm.spot_percentages, [s.id]: Math.min(100, Math.max(0, v)) },
                          });
                        }}
                      />
                      <Typography variant="caption">%</Typography>
                    </Box>
                  ))}
                  <Typography variant="caption" color={Object.values(segmentForm.spot_percentages || {}).reduce((a, b) => a + (Number(b) || 0), 0) === 100 ? 'success.main' : 'warning.main'}>
                    Total: {Object.values(segmentForm.spot_percentages || {}).reduce((a, b) => a + (Number(b) || 0), 0)}%
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" sx={{ mb: 0.5, color: 'var(--rs-text)', fontWeight: 600 }}>Day parts</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Configure day parts in Settings. Leave empty for all day.
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={segmentForm.daypart_ids || []}
                  onChange={(e) => setSegmentForm({ ...segmentForm, daypart_ids: e.target.value })}
                  renderValue={(sel) => sel.length ? sel.map((id) => dayparts.find((d) => d.id === id)?.name || id).join(', ') : 'All'}
                  displayEmpty
                >
                  {dayparts.map((dp) => (
                    <MenuItem key={dp.id} value={dp.id}>
                      <Checkbox checked={(segmentForm.daypart_ids || []).includes(dp.id)} />
                      <ListItemText primary={`${dp.name} (${dp.start_time}–${dp.end_time})`} />
                    </MenuItem>
                  ))}
                  {dayparts.length === 0 && (
                    <MenuItem value="" disabled>No day parts – add in Settings</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" sx={{ mb: 0.5, color: 'var(--rs-text)', fontWeight: 600 }}>Breaks</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={segmentForm.all_breaks}
                    onChange={(e) => setSegmentForm({ ...segmentForm, all_breaks: e.target.checked, selected_breaks: e.target.checked ? [] : segmentForm.selected_breaks })}
                  />
                }
                label="All breaks"
              />
              {!segmentForm.all_breaks && (() => {
                const campaignStationIds = toIdList(wizardCampaign?.enable_stations);
                const relevantBreaks = breaks.filter((b) => !campaignStationIds.length || hasId(campaignStationIds, b.station_id));
                const formatBreak = (b) => `${stations.find((s) => s.id === b.station_id)?.name || 'S' + b.station_id} · ${String(b.hour).padStart(2, '0')}:${String(b.minute || 0).padStart(2, '0')} · ${b.name}`;
                return (
                  <FormGroup row sx={{ mt: 1, flexWrap: 'wrap', gap: 0 }}>
                    {relevantBreaks.slice(0, 50).map((b) => (
                      <FormControlLabel
                        key={b.id}
                        control={
                          <Checkbox
                            checked={(segmentForm.selected_breaks || []).includes(b.id)}
                            onChange={(e) => {
                              const arr = segmentForm.selected_breaks || [];
                              setSegmentForm({
                                ...segmentForm,
                                selected_breaks: e.target.checked ? [...arr, b.id] : arr.filter((x) => x !== b.id),
                              });
                            }}
                          />
                        }
                        label={<Typography variant="body2">{formatBreak(b)}</Typography>}
                      />
                    ))}
                    {relevantBreaks.length > 50 && (
                      <Typography variant="caption" color="text.secondary">+ {relevantBreaks.length - 50} more breaks</Typography>
                    )}
                  </FormGroup>
                );
              })()}
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Template</InputLabel>
                <Select value={segmentForm.segment_template_id} onChange={(e) => setSegmentForm({ ...segmentForm, segment_template_id: e.target.value })} label="Template">
                  <MenuItem value="">None</MenuItem>
                  {segmentTemplates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddSegment}
                disabled={addingSegment}
                sx={{ bgcolor: '#a855f7', '&:hover': { bgcolor: '#9333ea' } }}
              >
                {addingSegment ? 'Adding...' : 'Add Segment'}
              </Button>
              <Button variant="outlined" size="small" onClick={handleSaveTemplate}>
                Save as Template
              </Button>
            </Grid>
          </Grid>
        </Section>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1, color: '#c084fc', fontWeight: 700 }}>
        Segments ({campaignSegments.length})
      </Typography>
      {campaignSegments.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed var(--rs-border)', borderRadius: 2 }}>
          <ScheduleIcon sx={{ color: 'var(--rs-muted)', fontSize: 30, mb: 0.5 }} />
          <Typography color="text.secondary">
            {readOnly ? 'No segments on this campaign.' : 'No segments yet. Add one above to put this campaign on the schedule.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={segmentSort.orderBy === 'name' ? segmentSort.order : false}>
                  <TableSortLabel active={segmentSort.orderBy === 'name'} direction={segmentSort.orderBy === 'name' ? segmentSort.order : 'asc'} onClick={createSortHandler(setSegmentSort)('name')}>Name</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={segmentSort.orderBy === 'start_date' ? segmentSort.order : false}>
                  <TableSortLabel active={segmentSort.orderBy === 'start_date'} direction={segmentSort.orderBy === 'start_date' ? segmentSort.order : 'asc'} onClick={createSortHandler(setSegmentSort)('start_date')}>Dates</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={segmentSort.orderBy === 'spots_per_day' ? segmentSort.order : false}>
                  <TableSortLabel active={segmentSort.orderBy === 'spots_per_day'} direction={segmentSort.orderBy === 'spots_per_day' ? segmentSort.order : 'asc'} onClick={createSortHandler(setSegmentSort)('spots_per_day')}>Spots/day</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={segmentSort.orderBy === 'positioning' ? segmentSort.order : false}>
                  <TableSortLabel active={segmentSort.orderBy === 'positioning'} direction={segmentSort.orderBy === 'positioning' ? segmentSort.order : 'asc'} onClick={createSortHandler(setSegmentSort)('positioning')}>Position</TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...campaignSegments].sort(getSortComparator(segmentSort.order, segmentSort.orderBy, (seg) => {
                if (segmentSort.orderBy === 'spots_per_day') return seg.settings?.run_in_all_breaks ? 'Every break' : (seg.spots_per_day ?? '');
                if (segmentSort.orderBy === 'start_date') return seg.start_date ?? '';
                return seg[segmentSort.orderBy] ?? '';
              })).map((seg) => (
                <TableRow key={seg.id}>
                  <TableCell>{seg.name}</TableCell>
                  <TableCell>{seg.start_date?.split('T')[0]} – {(seg.end_date && !seg.end_date.startsWith('9999-')) ? seg.end_date.split('T')[0] : 'Never'}</TableCell>
                  <TableCell>{seg.settings?.run_in_all_breaks ? 'Every break' : (seg.spots_per_day ?? '—')}</TableCell>
                  <TableCell>{seg.positioning || 'any'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" sx={{ color: '#22d3ee' }} onClick={() => setSelectedSegmentDetail(seg)} title="View details">
                      <Visibility fontSize="small" />
                    </IconButton>
                    <IconButton size="small" disabled={readOnly || seg.is_syndicated} sx={{ color: '#ff6b6b' }} onClick={() => handleDeleteSegment(seg.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  const stepContent = [renderDetailsStep(), renderSpotsStep(), renderSegmentsStep()][step];

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseWizard}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--rs-paper)',
            border: '1px solid var(--rs-border)',
            borderRadius: 3,
            minHeight: 'calc(var(--rs-vh) * 0.7)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '13px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #4fdcf3 0%, #0ea5c9 100%)',
                boxShadow: '0 6px 18px -6px rgba(34,211,238,0.55)',
              }}
            >
              <CampaignIcon sx={{ color: '#04222b' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ color: 'var(--rs-text)', lineHeight: 1.2 }}>
                {isEdit
                  ? `Edit Campaign — ${wizardCampaign?.name || campaign?.name || ''}`
                  : (wizardCampaign?.name ? `New Campaign — ${wizardCampaign.name}` : 'New Campaign')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Set up the details, load the spots, then build the schedule — all in one place.
              </Typography>
            </Box>
          </Box>
          <Stepper
            activeStep={step}
            nonLinear={!!wizardCampaign?.id}
            sx={{
              mt: 2.5,
              '& .MuiStepLabel-label': { color: 'var(--rs-muted)', fontWeight: 600 },
              '& .MuiStepLabel-label.Mui-active': { color: '#22d3ee' },
              '& .MuiStepLabel-label.Mui-completed': { color: '#22c55e' },
              '& .MuiStepIcon-root': { color: 'rgba(148,163,184,0.25)' },
              '& .MuiStepIcon-root.Mui-active': { color: '#22d3ee' },
              '& .MuiStepIcon-root.Mui-completed': { color: '#22c55e' },
            }}
          >
            {STEPS.map(({ label }, index) => {
              const locked = !wizardCampaign?.id && index > 0;
              const completed = index < step || (!!wizardCampaign?.id && index === 0 && step > 0);
              return (
                <Step key={label} completed={completed} disabled={locked}>
                  <StepButton
                    onClick={() => goToStep(index)}
                    disabled={locked}
                    optional={locked ? <Typography variant="caption">Create the campaign first</Typography> : null}
                  >
                    {label}
                  </StepButton>
                </Step>
              );
            })}
          </Stepper>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {stepContent}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid var(--rs-border)' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            Step {step + 1} of {STEPS.length} — {STEPS[step].label}
            {wizardCampaign?.id ? ` · Campaign #${wizardCampaign.id} saved` : ''}
          </Typography>
          <Button onClick={handleCloseWizard} disabled={saving || uploadingSpot || addingSegment}>Close</Button>
          {step > 0 && (
            <Button startIcon={<ArrowBack />} onClick={() => setStep(step - 1)}>
              Back
            </Button>
          )}
          {step === 0 && (
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              disabled={saving || (!readOnly && !formData.name?.trim())}
              onClick={handleDetailsNext}
              sx={{
                backgroundImage: 'linear-gradient(180deg, #4fdcf3 0%, #0ea5c9 100%)',
                color: '#04222b',
                fontWeight: 700,
                '&:hover': { backgroundImage: 'linear-gradient(180deg, #67e8f9 0%, #22d3ee 100%)' },
              }}
            >
              {readOnly ? 'Next' : saving ? 'Saving...' : wizardCampaign?.id ? 'Save & Continue' : 'Create & Continue'}
            </Button>
          )}
          {step === 1 && (
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={() => setStep(2)}
              sx={{
                backgroundImage: 'linear-gradient(180deg, #4fdcf3 0%, #0ea5c9 100%)',
                color: '#04222b',
                fontWeight: 700,
                '&:hover': { backgroundImage: 'linear-gradient(180deg, #67e8f9 0%, #22d3ee 100%)' },
              }}
            >
              Continue to Schedule
            </Button>
          )}
          {step === 2 && (
            <Button
              variant="contained"
              startIcon={<Check />}
              onClick={handleFinish}
              sx={{
                backgroundImage: 'linear-gradient(180deg, #34d275 0%, #1fa855 100%)',
                color: '#04150b',
                fontWeight: 700,
                '&:hover': { backgroundImage: 'linear-gradient(180deg, #46e087 0%, #26bb61 100%)' },
              }}
            >
              Finish
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AudioEditor
        asset={editingCueSpot ? {
          id: editingCueSpot.id,
          title: editingCueSpot.name,
          filepath: editingCueSpot.filepath,
          cue_in: editingCueSpot.cue_in || 0,
          cue_out: editingCueSpot.cue_out || null,
        } : null}
        isOpen={spotCueEditorOpen}
        markerMode="cue"
        saveDirectly={false}
        onClose={() => {
          setSpotCueEditorOpen(false);
          setEditingCueSpot(null);
        }}
        onSave={handleSaveSpotCues}
      />

      {/* Segment Detail Dialog */}
      <Dialog open={!!selectedSegmentDetail} onClose={() => setSelectedSegmentDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#22d3ee' }}>Segment Details</DialogTitle>
        <DialogContent>
          {selectedSegmentDetail && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography>{selectedSegmentDetail.name || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Dates</Typography>
                  <Typography>{selectedSegmentDetail.start_date?.split('T')[0]} – {(selectedSegmentDetail.end_date && !String(selectedSegmentDetail.end_date).startsWith('9999-')) ? selectedSegmentDetail.end_date.split('T')[0] : 'Never'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Spots per day</Typography>
                  <Typography>{selectedSegmentDetail.settings?.run_in_all_breaks ? 'Every break' : (selectedSegmentDetail.spots_per_day ?? '—')}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Position in break</Typography>
                  <Typography>{selectedSegmentDetail.positioning || 'any'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Spot selection</Typography>
                  <Typography>{selectedSegmentDetail.spot_selection || 'random'}</Typography>
                </Grid>
                {selectedSegmentDetail.settings?.specific_spot_id && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Specific spot</Typography>
                    <Typography>{campaignSpots.find((s) => s.id === selectedSegmentDetail.settings?.specific_spot_id)?.name || `ID ${selectedSegmentDetail.settings.specific_spot_id}`}</Typography>
                  </Grid>
                )}
                {(() => {
                  const dp = selectedSegmentDetail.daypart_ids;
                  const arr = Array.isArray(dp) ? dp : (typeof dp === 'string' ? (() => { try { return JSON.parse(dp); } catch { return []; } })() : []);
                  return arr.length > 0 ? (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Day parts</Typography>
                      <Typography>{arr.map((id) => dayparts.find((d) => d.id === id)?.name || id).join(', ')}</Typography>
                    </Grid>
                  ) : null;
                })()}
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Breaks</Typography>
                  <Typography>
                    {(() => {
                      const sb = selectedSegmentDetail.selected_breaks;
                      const arr = Array.isArray(sb) ? sb : (typeof sb === 'string' ? (() => { try { return JSON.parse(sb); } catch { return []; } })() : []);
                      return !arr.length ? 'All breaks' : `${arr.length} selected`;
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Weekdays</Typography>
                  <Typography>
                    {(() => {
                      const wd = selectedSegmentDetail.weekdays;
                      const arr = Array.isArray(wd) ? wd : (typeof wd === 'string' ? (() => { try { return JSON.parse(wd); } catch { return []; } })() : []);
                      if (!arr.length || arr.length === 7) return 'All days';
                      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                      return arr.map((i) => days[i]).filter(Boolean).join(', ') || '—';
                    })()}
                  </Typography>
                </Grid>
                {selectedSegmentDetail.segment_template_id && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Template</Typography>
                    <Typography>{segmentTemplates.find((t) => t.id === selectedSegmentDetail.segment_template_id)?.name || `ID ${selectedSegmentDetail.segment_template_id}`}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedSegmentDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
